import { dayText } from './hint';
import { mskDate } from '../ui/moscow';
import type { PlanState } from './band';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПЛАНКА ОСТАТКА ПОДПИСКИ В ШАПКЕ (доп-ТЗ, п. 3).
 *
 * `data-d="остаток-тарифа"` — капсула на месте бывшего номера сборки.
 * Ведёт в «Счета и оплаты».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ОНА ВЫТЕСНИЛА НОМЕР СБОРКИ, А НЕ ВСТАЛА РЯДОМ.
 *
 * Номер сборки отвечает на вопрос, который задают раз в месяц и только
 * свои: «доехала ли выкатка». Остаток подписки отвечает на вопрос,
 * который у клиента стоит каждый день. Место в шапке одно, и занимать
 * его должен второй.
 *
 * Номер сборки никуда не делся: он в ящике («Сборка») и на своём
 * экране `/rk/version`, куда и ведёт полоса «пакет устарел».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СТУПЕНИ — ИЗ ПЕРЕДАЧИ, А НЕ ПРИДУМАНЫ.
 *
 *   больше 7 суток → ok   · «24 дня»
 *   4–7 суток      → warn · «6 дней» + кнопка «Продлить тариф»
 *   1–3 суток      → bad  · «2 дня» + кнопка «Продлить тариф»
 *   0 и меньше     → over · «подписка кончилась»
 *   заморожено     → warn · «заморожено», сутки не убывают
 * ═══════════════════════════════════════════════════════════════════════
 */
export type Step = 'ok' | 'warn' | 'bad' | 'over';

export interface Chip {
  step: Step;
  text: string;
  /**
   * Надпись для узкого экрана.
   *
   * ═════════════════════════════════════════════════════════════════
   * ТЕПЕРЬ ОНА РАВНА `text`, И ПОЛЕ ОСТАВЛЕНО НАРОЧНО.
   *
   * Поле заводилось, когда в капсуле стояло «24 суток тарифа» —
   * семнадцать знаков, выдавливавших на 390 имя кабинета. Пакет
   * 25.08 срезал хвост, осталось «24 дня», и ужимать нечего:
   * эталон печатает на 390 то же, что на 1440.
   *
   * Убрать поле совсем — значит переписать всех, кто его читает,
   * ради нуля выигрыша. Пусть лежит равным `text`: разойдутся
   * они только тогда, когда кто-то нарочно это сделает, и тогда
   * поле снова понадобится.
   * ═════════════════════════════════════════════════════════════════
   */
  short: string;
  /** Всплывающая подпись всегда называет дату. */
  title: string;
  /**
   * Показывать ли рядом кнопку «Продлить тариф».
   *
   * ═════════════════════════════════════════════════════════════════
   * ПОРОГ ТОТ ЖЕ, ЧТО У ОРАНЖЕВОЙ СТУПЕНИ, И ЭТО НЕ СОВПАДЕНИЕ.
   *
   * Капсула желтеет тогда же, когда появляется кнопка: цвет говорит
   * «пора», кнопка отвечает «вот куда нажать». Развести пороги
   * значило бы получить неделю, когда капсула тревожится, а сделать
   * с этим на месте нечего.
   * ═════════════════════════════════════════════════════════════════
   */
  renew: boolean;
}

/** Границы ступеней. Числа из передачи, и они здесь одни на весь кабинет. */
export const WARN_FROM = 7;
export const BAD_FROM = 3;

export function leftChip(current: PlanState | null): Chip | null {
  /**
   * ПОДПИСКИ НЕТ — ПЛАНКИ НЕТ.
   *
   * Так выглядит пространство до подключения кабинета: пробный ещё
   * не выдан. Капсула «подписка кончилась» у новичка, который ещё
   * ничего не начинал, — это сообщение о поломке там, где всё идёт
   * по порядку.
   */
  if (current === null) return null;

  const until = dateText(current.expiresAt);

  /**
   * ЗАМОРОЗКА ВАЖНЕЕ СРОКА, И СУТКИ ПРИ НЕЙ НЕ УБЫВАЮТ.
   *
   * Заморозка останавливает и управление, и счёт: оплаченное время
   * ждёт возвращения. Показывать «осталось 12 суток» на замороженной
   * подписке значило бы обещать, что через двенадцать суток что-то
   * произойдёт, — а не произойдёт ничего.
   */
  if (current.frozen) {
    return { step: 'warn', text: 'заморожено', short: 'заморожено', title: until ?? 'Подписка заморожена', renew: true };
  }

  if (current.health === 'expired') {
    return {
      step: 'over',
      text: 'подписка кончилась',
      short: 'кончилась',
      title: until ?? 'Подписка кончилась',
      renew: true,
    };
  }

  /**
   * БЕССРОЧНАЯ ПОДПИСКА МОЛЧИТ. Сказать о ней нечего: суток нет,
   * даты нет, тревоги нет. Пустая капсула хуже отсутствующей.
   */
  if (current.daysLeft === null) return null;

  if (current.daysLeft <= 0) {
    return {
      step: 'over',
      text: 'подписка кончилась',
      short: 'кончилась',
      title: until ?? 'Подписка кончилась',
      renew: true,
    };
  }

  /**
   * ═══════════════════════════════════════════════════════════════════
   * НАДПИСЬ — ТОЛЬКО ЧИСЛО И СЛОВО: «24 дня».
   *
   * Было «24 суток тарифа». Пакет 25.08 (раздел 1) убрал оба лишних
   * слова, и по делу: «тарифа» повторяет то, что и так написано
   * в подсказке, а «осталось» — то, что и так понятно из числа
   * рядом с цветной точкой. Семнадцать знаков в шапке, где место
   * считают, превратились в семь.
   *
   * КОРОТКОЙ НАДПИСИ БОЛЬШЕ НЕТ. Она заводилась ровно затем, чтобы
   * ужать «24 суток тарифа» до «24 сут» на 390. Ужимать «24 дня»
   * не от чего: эталон на узком экране печатает то же самое.
   * ═══════════════════════════════════════════════════════════════════
   */
  const надпись = dayText(current.daysLeft);

  return {
    step: current.daysLeft > WARN_FROM ? 'ok' : current.daysLeft > BAD_FROM ? 'warn' : 'bad',
    text: надпись,
    short: надпись,
    title: until ?? 'Подписка действует',
    renew: current.daysLeft <= WARN_FROM,
  };
}

/**
 * ДАТА ВО ВСПЛЫВАЮЩЕЙ ПОДПИСИ — ЧИСЛОМ, А НЕ «ЧЕРЕЗ 24 СУТОК».
 *
 * Сутки в капсуле отвечают на «сколько осталось», дата — на «до какого
 * числа платить». Второе человек переписывает в календарь, и считать
 * его самому из «24 суток» он не должен.
 */
function dateText(iso: string | null): string | null {
  if (iso === null) return null;
  if (Number.isNaN(new Date(iso).getTime())) return null;

  // Дата — тем же помощником, что и везде в кабинете: время по Москве,
  // а не по часам браузера. Клиент в Новосибирске и поддержка
  // в Москве обязаны называть одну и ту же дату окончания
  return `оплачено до ${mskDate(iso)}`;
}
